import { apiUtils } from "../../../utils/apiUtils";
import type { BasePagingResponse, BaseResponse } from "../../../lib/api-contract";
import type {
  DeleteEGiftCardInfoResponse,
  EGiftCardAvailableBalanceResponse,
  EGiftCardListItemResponse,
  EGiftCardListRequestForAdmin,
  EGiftCardStatisticsResponse,
  ExistingEGiftCardCampaignResponse,
  GetEGiftCardDetailResponse,
  RecallEGiftCardResponse,
  RestockEGiftCardResponse,
  CreateEGiftCardPayload,
  UpdateEGiftCardPayload,
  UpsertEGiftCardResponse,
} from "../types/eGiftCard.types";

export async function getEGiftCardList(
  request: EGiftCardListRequestForAdmin,
  signal?: AbortSignal,
): Promise<BasePagingResponse<EGiftCardListItemResponse>> {
  const { paging, list } = await apiUtils.getData<BasePagingResponse<EGiftCardListItemResponse>>(
    "/v1/portal/e-gift-card",
    "Failed to fetch e-gift cards",
    { params: request, signal },
  );

  return {
    paging: {
      count: paging?.count ?? 0,
      index: paging?.index ?? request.index,
      size: paging?.size ?? request.size,
      total: paging?.total ?? 0,
    },
    list: list,
  };
}

export function getEGiftCardDetail(
  id: string,
  signal?: AbortSignal,
): Promise<GetEGiftCardDetailResponse> {
  return apiUtils.getData<GetEGiftCardDetailResponse>(
    `/v1/portal/e-gift-card/${id}`,
    "Failed to fetch e-gift card detail",
    { signal },
  );
}

export function getEGiftCardStatistics(): Promise<EGiftCardStatisticsResponse> {
  return apiUtils.getData<EGiftCardStatisticsResponse>(
    "/v1/portal/e-gift-card/statistics",
    "Failed to fetch e-gift card statistics",
  );
}

export function getEGiftCardAvailableBalance(): Promise<EGiftCardAvailableBalanceResponse> {
  return apiUtils.getData<EGiftCardAvailableBalanceResponse>(
    "/v1/portal/organization-fund/balance",
    "Failed to fetch e-gift card available balance",
  );
}

export function createEGiftCard(request: CreateEGiftCardPayload): Promise<BaseResponse<unknown>> {
  return apiUtils.post<unknown, CreateEGiftCardPayload>("/v1/portal/e-gift-card", request);
}

export async function updateEGiftCard(
  id: string,
  request: UpdateEGiftCardPayload,
): Promise<UpsertEGiftCardResponse> {
  await apiUtils.putData<unknown, UpdateEGiftCardPayload>(
    `/v1/portal/e-gift-card/${id}`,
    request,
    "Failed to update e-gift card",
  );

  return {
    success: true,
    message: "E-gift card updated successfully",
  };
}

interface EGiftCardRestockApiResponse {
  id: string;
  title: string;
  quantity: number;
  remaining_quantity: number;
  used_quantity: number;
}

export async function restockEGiftCard(
  eGiftCardId: string,
  quantity: number,
  signal?: AbortSignal,
): Promise<RestockEGiftCardResponse> {
  const query = new URLSearchParams({ quantity: String(quantity) });
  const response = await apiUtils.patch<EGiftCardRestockApiResponse>(
    `/v1/portal/e-gift-card/${encodeURIComponent(eGiftCardId)}/restock?${query.toString()}`,
    undefined,
    { signal },
  );
  const restockedCard = apiUtils.unwrap(response, "Failed to restock e-gift card");

  return {
    coupon_id: restockedCard.id,
    coupon_name: restockedCard.title,
    created_quantity: restockedCard.quantity,
    available_quantity: restockedCard.remaining_quantity,
    restock_quantity: null,
    success: true,
    message: response.message ?? "E-gift card restocked successfully",
  };
}

export function getEGiftCardsByMerchantBrandId(
  merchantBrandId: string,
  merchantBrandItemId: string,
  pointRequired: number,
  couponId?: string,
  signal?: AbortSignal,
): Promise<ExistingEGiftCardCampaignResponse[]> {
  return apiUtils.getData<ExistingEGiftCardCampaignResponse[]>(
    `/v1/portal/e-gift-card/brand/${merchantBrandId}`,
    "Failed to fetch e-gift card campaigns",
    {
      params: {
        merchantBrandItemId,
        pointRequired,
        ...(couponId ? { couponId } : {}),
      },
      signal,
    },
  );
}

export function getDeleteEGiftCardInfo(id: string): Promise<DeleteEGiftCardInfoResponse> {
  return apiUtils.getData<DeleteEGiftCardInfoResponse>(
    `/v1/portal/coupon/delete-info/${id}`,
    "Failed to fetch e-gift card delete information",
  );
}

export function recallEGiftCard(id: string): Promise<RecallEGiftCardResponse> {
  return apiUtils
    .delete<RecallEGiftCardResponse>(`/v1/portal/coupon/recall/${id}`)
    .then((response) => apiUtils.unwrap(response, "Failed to recall e-gift card"));
}
